import React, { useState } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

/**
 * OpzionatoBadge - Componente riutilizzabile per mostrare lo stato OPZIONATO
 * 
 * Props:
 * - opzionato_ae: boolean - OPZIONATO in colonna AE (1 bandierina) - USATO
 * - opzionato_af: boolean - OPZIONATO in colonna AF (2 bandierine se anche AE) - USATO
 * - opzionato_aj: boolean - OPZIONATO in colonna AJ - NUOVO/4VANTAGE
 * - variant: 'badge' | 'inline' | 'compact' - Stile di visualizzazione
 * - showTooltip: boolean - Mostra tooltip al hover (default: true)
 * - className: string - Classi CSS aggiuntive
 */
const OpzionatoBadge = ({ 
  opzionato_ae = false, 
  opzionato_af = false, 
  opzionato_aj = false,
  variant = 'badge',
  showTooltip = true,
  className = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Calcola se è opzionato e quante bandierine mostrare
  // NUOVO/4VANTAGE usa opzionato_aj, USATO usa opzionato_ae/af
  const isOpzionato = opzionato_ae || opzionato_af || opzionato_aj;
  const bandierineCount = opzionato_ae && opzionato_af ? 2 : (opzionato_ae || opzionato_aj ? 1 : 0);
  
  if (!isOpzionato) return null;
  
  // Messaggio di urgenza
  const urgencyMessage = {
    title: "⚠️ Attenzione: veicolo sotto l'attenzione di un altro cliente",
    body: "Procedi velocemente con l'affidamento per non perdere la priorità. Invia i documenti per metterti in coda.",
    footer: "💡 L'opzione e l'ordine sono subordinati all'approvazione del credito."
  };
  
  // Variante BADGE (usata in vetrina e catalogo)
  if (variant === 'badge') {
    return (
      <div 
        className={`relative cursor-pointer ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center gap-1 bg-amber-100 border-2 border-amber-400 rounded-lg px-2 py-1 shadow-md">
          <span className="text-lg">🚩</span>
          {bandierineCount === 2 && <span className="text-lg">🚩</span>}
          <span className="text-xs font-bold text-amber-700 ml-1">OPZIONATA</span>
        </div>
        
        {/* Tooltip */}
        {showTooltip && isHovered && (
          <div className="absolute top-full right-0 mt-2 w-72 bg-amber-50 border-2 border-amber-400 rounded-lg shadow-xl p-3 z-50">
            <div className="flex items-start gap-2">
              <div className="text-amber-600 mt-0.5">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">
                  {urgencyMessage.title}
                </p>
                <p className="text-xs text-amber-700 leading-relaxed">
                  {urgencyMessage.body}
                </p>
                <p className="text-xs text-amber-600 mt-2 font-medium">
                  {urgencyMessage.footer}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Variante INLINE (usata nelle card delle offerte)
  if (variant === 'inline') {
    return (
      <div 
        className={`relative ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center gap-2 bg-amber-100 border border-amber-400 rounded-lg px-3 py-2 cursor-pointer">
          <div className="flex items-center gap-1">
            <span className="text-base">🚩</span>
            {bandierineCount === 2 && <span className="text-base">🚩</span>}
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">
              Veicolo già opzionato
            </p>
            <p className="text-xs text-amber-700">
              Procedi con l&apos;affidamento per non perdere priorità
            </p>
          </div>
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        
        {/* Tooltip dettagliato */}
        {showTooltip && isHovered && (
          <div className="absolute bottom-full left-0 mb-2 w-80 bg-amber-50 border-2 border-amber-400 rounded-lg shadow-xl p-3 z-50">
            <div className="flex items-start gap-2">
              <div className="text-amber-600 mt-0.5">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">
                  {urgencyMessage.title}
                </p>
                <p className="text-xs text-amber-700 leading-relaxed">
                  {urgencyMessage.body}
                </p>
                <p className="text-xs text-amber-600 mt-2 font-medium">
                  {urgencyMessage.footer}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Variante COMPACT (piccola, solo icona)
  if (variant === 'compact') {
    return (
      <div 
        className={`relative inline-flex items-center cursor-pointer ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center bg-amber-100 border border-amber-400 rounded px-1.5 py-0.5">
          <span className="text-sm">🚩</span>
          {bandierineCount === 2 && <span className="text-sm">🚩</span>}
        </div>
        
        {/* Tooltip */}
        {showTooltip && isHovered && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-amber-50 border-2 border-amber-400 rounded-lg shadow-xl p-2 z-50">
            <p className="text-xs font-semibold text-amber-800">
              ⚠️ Veicolo opzionato da altro cliente
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Procedi velocemente con l&apos;affidamento
            </p>
          </div>
        )}
      </div>
    );
  }
  
  return null;
};

export default OpzionatoBadge;
